import { APP_INITIALIZER, enableProdMode, importProvidersFrom } from '@angular/core';
import { environment } from '@environment';
import { AppInitService } from './app/core/services/app-initialization.service';
import { QuillHelperService } from '@sb-tools/typescript/services/quill/quill-helper.service';
import { HTTP_INTERCEPTORS, HttpBackend, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { Interceptor } from './app/core/services/interceptor.service';
import { RedirectGuard } from './app/core/redirect.guard';
import { RedirectToMyTicketsGuard } from './app/core/redirect-to-my-tickets.guard';
import { DROPZONE_CONFIG, DropzoneConfigInterface, DropzoneModule } from 'ngx-dropzone-wrapper';
import { LinkyModule, LinkyPipe } from 'ngx-linky';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { UiSwitchModule } from 'ngx-ui-switch';
import { SimpleNotificationsModule } from 'angular2-notifications';
import { provideRouter } from '@angular/router';
import { routes } from './app/app.routes';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { AvatarModule } from 'ngx-avatars';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { ModalModule } from 'ngx-bootstrap/modal';
import { PickerModule } from '@ctrl/ngx-emoji-mart';
import { EmojiModule } from '@ctrl/ngx-emoji-mart/ngx-emoji';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { TreeModule } from '@ali-hm/angular-tree-component';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { ClipboardModule } from 'ngx-clipboard';
import { QuillModule } from 'ngx-quill';
import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { NgSelectModule } from '@ng-select/ng-select';
import { AutosizeModule } from 'ngx-autosize';
import { TimepickerModule } from 'ngx-bootstrap/timepicker';
import { NgxSummernoteModule } from 'ngx-summernote';
import { AppComponent } from './app/app.component';
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';
import { defineLocale, fiLocale } from 'ngx-bootstrap/chronos';
import { daLocale, nbLocale, svLocale } from 'ngx-bootstrap/locale';
import packageJson from 'package.json';

// Pipes for DI
import { EncodeHtmlSymbolsPipe } from '@sb-tools/typescript/pipes/encode-html-symbols.pipe';
import { NewLinePipe } from '@sb-tools/typescript/pipes/new-line.pipe';
import { HttpLinkPipe } from '@sb-tools/typescript/pipes/ticket/http-link.pipe';
import { PrepareMessagePipe } from '@sb-tools/typescript/pipes/ticket/prepare-message.pipe';
import { HideContentPipe } from './app/shared/pipes/hide-content.pipe';
import { ShortNumberPipe } from './app/shared/pipes/short-number.pipe';

// Define locales for ngx-bootstrap datepicker
defineLocale('nb', nbLocale);
defineLocale('sv', svLocale);
defineLocale('da', daLocale);
defineLocale('fi', fiLocale);

// App initialization factory
export function initApp(firstLoadService: AppInitService) {
    return () => {
        return firstLoadService.initializeMyApp();
    };
}

// Quill initialization factory
export function initQuill(quillHelperService: QuillHelperService) {
    return () => quillHelperService.initQuillRegisters();
}

// Translation loader factory
export function HttpLoaderFactory(httpBackend: HttpBackend) {
    return new MultiTranslateHttpLoader(httpBackend, [
        { prefix: '/assets/i18n/', suffix: '.json?' + packageJson.version },
        { prefix: '/sb-tools/assets/i18n/', suffix: '.json?' + packageJson.version },
        { prefix: '/sb-modules/Frontend-form-field-module/assets/i18n/', suffix: '.json?' + packageJson.version },
        { prefix: '/sb-modules/Frontend-Document-Viewer-Module/assets/i18n/', suffix: '.json?' + packageJson.version },
        { prefix: '/sb-modules/Frontend-GIF-Module/assets/i18n/', suffix: '.json?' + packageJson.version },
        { prefix: '/sb-modules/Frontend-Reply-Templates-Module/assets/i18n/', suffix: '.json?' + packageJson.version },
        { prefix: '/sb-modules/Frontend-Settings-Menu-Module/assets/i18n/', suffix: '.json?' + packageJson.version },
        { prefix: '/sb-modules/Frontend-Table-Module/assets/i18n/', suffix: '.json?' + packageJson.version },
        { prefix: '/sb-modules/Frontend-User-Avatar-Module/assets/i18n/', suffix: '.json?' + packageJson.version },
    ]);
}

const DEFAULT_DROPZONE_CONFIG: DropzoneConfigInterface = {
    maxFilesize: 50,
    acceptedFiles: 'image/*',
};

const ngxUiSwitchConfig = {
    size: 'small',
    color: '#fed030',
};

if (environment.production) {
    enableProdMode();
}

bootstrapApplication(AppComponent, {
    providers: [
        importProvidersFrom(
            BrowserModule,
            UiSwitchModule.forRoot(ngxUiSwitchConfig),
            SimpleNotificationsModule.forRoot({
                position: ['bottom', 'right'],
                timeOut: 3000,
                lastOnBottom: true,
                showProgressBar: true,
                pauseOnHover: true,
            }),
            TooltipModule.forRoot(),
            BsDropdownModule.forRoot(),
            AvatarModule,
            InfiniteScrollModule,
            FormsModule,
            ReactiveFormsModule,
            CollapseModule.forRoot(),
            ModalModule.forRoot(),
            PickerModule,
            EmojiModule,
            PopoverModule.forRoot(),
            TreeModule,
            BsDatepickerModule.forRoot(),
            TabsModule.forRoot(),
            ClipboardModule,
            LinkyModule,
            DropzoneModule,
            QuillModule,
            TranslateModule.forRoot({
                loader: {
                    provide: TranslateLoader,
                    useFactory: HttpLoaderFactory,
                    deps: [HttpBackend],
                },
            }),
            TypeaheadModule.forRoot(),
            NgSelectModule,
            AutosizeModule,
            TimepickerModule,
            NgxSummernoteModule,
            NgOptimizedImage,
        ),
        {
            provide: APP_INITIALIZER,
            useFactory: initApp,
            multi: true,
            deps: [AppInitService],
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initQuill,
            multi: true,
            deps: [QuillHelperService],
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: Interceptor,
            multi: true,
        },
        RedirectGuard,
        RedirectToMyTicketsGuard,
        {
            provide: DROPZONE_CONFIG,
            useValue: DEFAULT_DROPZONE_CONFIG,
        },
        // Pipes that are injected via constructor
        LinkyPipe,
        EncodeHtmlSymbolsPipe,
        NewLinePipe,
        HttpLinkPipe,
        PrepareMessagePipe,
        HideContentPipe,
        ShortNumberPipe,
        provideHttpClient(withInterceptorsFromDi()),
        importProvidersFrom(CommonModule),
        provideAnimations(),
        provideRouter(routes),
    ],
}).catch((err) => {
    return console.error(err);
});
